import { ComparisonOperatorDTOKeywordEnum } from "@eu.mdotm.api/smart-workspaces";

/**
 * # `exist` is one keyword and two conditions
 *
 * Every other comparison operator takes values the user supplies — one for `gt`, two for `btw`, a list
 * for `in`. The presence operator asks whether the concept has a value at all, and *which way round* it
 * asks travels in its argument rather than in a second keyword: `[true]` keeps the items that have a
 * value, `[false]` the items whose value is missing.
 *
 * So one entry in a concept's `allowedComparisonOperators` is two things to pick from, and picking one is
 * the whole condition — the argument *is* the choice, so there is no value control after it. That is the
 * shape a {@link ComparisonChoice} carries, and every site that shows, stores or reopens a condition goes
 * through this module rather than special-casing the keyword itself: the operator select that offers two
 * entries for one keyword, the tuple the chain re-shapes when the operator changes, the value control that
 * is suppressed, and the panel that reopens a persisted filter on the right one of the two.
 *
 * The argument is a real boolean on the wire — the engine validates it as one and rejects an empty
 * argument list — so it is stored in `comparisonValue` exactly as it is sent.
 */
export type ComparisonChoice = {
	keyword: ComparisonOperatorDTOKeywordEnum;
	/** For `exist`, the argument the choice stands for: `true` has a value, `false` is missing. */
	presence?: boolean;
};

/** Whether this operator's argument is the choice itself rather than a value the user enters. */
export function isPresenceComparison(keyword: ComparisonOperatorDTOKeywordEnum | null | undefined): boolean {
	return keyword === ComparisonOperatorDTOKeywordEnum.Exist;
}

/**
 * What a concept's allowed operator offers the user: `exist` offers both directions, everything else
 * offers itself.
 *
 * "Is not empty" comes first, so the affirmative reading is the one a list scanned top-down meets.
 */
export function comparisonChoicesOf(keyword: ComparisonOperatorDTOKeywordEnum): ComparisonChoice[] {
	return isPresenceComparison(keyword)
		? [
				{ keyword, presence: true },
				{ keyword, presence: false },
			]
		: [{ keyword }];
}

/**
 * The translation key a choice is labelled with.
 *
 * Spelled out as a return type rather than left as `string`, because `t` is keyed on the literal union
 * `en.json` generates: a key this returns that nobody wrote is a type error here rather than a raw
 * `comparator.…` on screen.
 */
export function comparisonChoiceLabelKey(
	choice: ComparisonChoice,
): `comparator.${Exclude<ComparisonOperatorDTOKeywordEnum, "exist">}` | `comparator.exist.${"present" | "missing"}` {
	return isPresenceComparison(choice.keyword)
		? `comparator.exist.${choice.presence ? "present" : "missing"}`
		: `comparator.${choice.keyword as Exclude<ComparisonOperatorDTOKeywordEnum, "exist">}`;
}

/**
 * A choice as the operator select's value, which has to be a single comparable — the two `exist` entries
 * are one keyword and would otherwise be indistinguishable to it.
 */
export function comparisonChoiceId(choice: ComparisonChoice): string {
	return isPresenceComparison(choice.keyword) ? `${choice.keyword}:${choice.presence}` : choice.keyword;
}

/** The choice a persisted condition is on: its keyword, plus the direction read back out of its argument. */
export function comparisonChoiceOf(
	keyword: ComparisonOperatorDTOKeywordEnum | null | undefined,
	comparisonValue: unknown,
): ComparisonChoice | null {
	if (keyword == null) {
		return null;
	}
	if (!isPresenceComparison(keyword)) {
		return { keyword };
	}
	// A filter reopened before the argument was written — or written by something other than this panel —
	// has no direction to show, so the select opens on neither entry rather than guessing one.
	const presence = Array.isArray(comparisonValue) ? comparisonValue[0] : undefined;
	return typeof presence === "boolean" ? { keyword, presence } : { keyword };
}
